import { CommonMessage } from '@frontend/lang';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { reportBug } from '../index';
import { goBackAndResetError } from './error-boundary-navigation';
import './sentry-error-boundary.css';

type ReportState = 'idle' | 'open' | 'submitting' | 'success' | 'error';

export interface MobileErrorFallbackProps {
    error: Error;
    eventId: string;
    resetError: () => void;
}

export function MobileErrorFallback({ error: _error, eventId, resetError }: MobileErrorFallbackProps) {
    const intl = useIntl();

    const [reportState, setReportState] = useState<ReportState>('idle');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        setReportState('submitting');
        const result = await reportBug({ message, name, email, associatedEventId: eventId });
        setReportState(result ? 'success' : 'error');
    }

    return (
        <div className='seb-mobile-overlay'>
            <div className='seb-mobile-header'>
                <div className='seb-mobile-icon'>⚠</div>
                <h1 className='seb-mobile-title'>
                    <FormattedMessage
                        id='SentryErrorBoundary.Title'
                        description='Title shown on the error boundary screen'
                        defaultMessage='Oops!'
                    />
                </h1>
                <p className='seb-mobile-description'>
                    <FormattedMessage
                        id='SentryErrorBoundary.Description'
                        description='Body text on the error boundary screen, apologising and prompting the user to go back or contact support'
                        defaultMessage='It seems like there is something wrong with the page you requested. We are very sorry for the inconvenience. You can go back where you came from and try again. Or if this keeps happening, please contact us.'
                    />
                </p>
            </div>

            <div className='seb-mobile-body'>
                {reportState === 'success' && (
                    <p className='seb-success-msg'>
                        {CommonMessage.ERRORS.BUG_REPORT.FORM_SUCCESS((chunks) => (
                            <strong>{chunks}</strong>
                        ))}
                    </p>
                )}

                {(reportState === 'open' || reportState === 'submitting' || reportState === 'error') && (
                    <form
                        id='mobile-fb-form'
                        onSubmit={handleSubmit}
                        className='seb-form'>
                        <div className='seb-field'>
                            <label
                                htmlFor='mobile-fb-name'
                                className='seb-label'>
                                {CommonMessage.FORMS.NAME}
                            </label>
                            <input
                                id='mobile-fb-name'
                                type='text'
                                className='seb-input seb-input-mobile'
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={intl.formatMessage({
                                    id: 'SentryErrorBoundary.Form.NamePlaceholder',
                                    description: 'Placeholder for the name field in the bug report form',
                                    defaultMessage: 'Your name (optional)'
                                })}
                                disabled={reportState === 'submitting'}
                                autoComplete='name'
                            />
                        </div>
                        <div className='seb-field'>
                            <label
                                htmlFor='mobile-fb-email'
                                className='seb-label'>
                                {CommonMessage.ERRORS.BUG_REPORT.FORM_EMAIL_LABEL}
                            </label>
                            <input
                                id='mobile-fb-email'
                                type='email'
                                inputMode='email'
                                className='seb-input seb-input-mobile'
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={intl.formatMessage({
                                    id: 'SentryErrorBoundary.Form.EmailPlaceholder',
                                    description: 'Placeholder for the email field in the bug report form',
                                    defaultMessage: 'your@email.com (optional)'
                                })}
                                disabled={reportState === 'submitting'}
                                autoComplete='email'
                            />
                        </div>
                        <div className='seb-field'>
                            <label
                                htmlFor='mobile-fb-message'
                                className='seb-label'>
                                {CommonMessage.ERRORS.BUG_REPORT.FORM_MESSAGE_LABEL} <span className='seb-required'>*</span>
                            </label>
                            <textarea
                                id='mobile-fb-message'
                                className='seb-textarea seb-input-mobile'
                                rows={4}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder={intl.formatMessage({
                                    id: 'SentryErrorBoundary.Form.MessagePlaceholder',
                                    description: 'Placeholder for the message field in the bug report form',
                                    defaultMessage: 'Describe what you were doing when the error occurred...'
                                })}
                                required
                                disabled={reportState === 'submitting'}
                            />
                        </div>
                        {reportState === 'error' && <p className='seb-error-msg'>{CommonMessage.ERRORS.BUG_REPORT.FORM_ERROR}</p>}
                    </form>
                )}
            </div>

            <div className='seb-mobile-actions'>
                {reportState === 'idle' && (
                    <>
                        <button
                            type='button'
                            className='seb-mobile-btn seb-mobile-btn-primary'
                            onClick={() => goBackAndResetError(resetError)}>
                            {CommonMessage.BUTTONS.GO_BACK}
                        </button>
                        <button
                            type='button'
                            className='seb-mobile-btn seb-mobile-btn-ghost'
                            onClick={() => setReportState('open')}>
                            {CommonMessage.ERRORS.BUG_REPORT.BUTTON}
                        </button>
                    </>
                )}

                {(reportState === 'open' || reportState === 'submitting' || reportState === 'error') && (
                    <>
                        <button
                            type='submit'
                            form='mobile-fb-form'
                            disabled={reportState === 'submitting' || !message.trim()}
                            className='seb-mobile-btn seb-mobile-btn-primary'
                            onClick={handleSubmit}>
                            {reportState === 'submitting' && (
                                <span
                                    className='seb-spinner-sm'
                                    role='status'
                                />
                            )}
                            {reportState === 'submitting' ? CommonMessage.ERRORS.BUG_REPORT.FORM_SENDING : CommonMessage.ERRORS.BUG_REPORT.FORM_SUBMIT}
                        </button>
                        <button
                            type='button'
                            className='seb-mobile-btn seb-mobile-btn-ghost'
                            onClick={() => setReportState('idle')}
                            disabled={reportState === 'submitting'}>
                            {CommonMessage.BUTTONS.CANCEL}
                        </button>
                    </>
                )}

                {reportState === 'success' && (
                    <button
                        type='button'
                        className='seb-mobile-btn seb-mobile-btn-primary'
                        onClick={() => goBackAndResetError(resetError)}>
                        {CommonMessage.BUTTONS.GO_BACK}
                    </button>
                )}
            </div>
        </div>
    );
}
